import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { alpha3ToAlpha2 } from 'i18n-iso-countries';
import { normalizeCapabilities } from '@rsos/capstone';
import { JURISDICTION_VIEW } from '@rsos/capstone/src/constants/capabilities';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { fetchPsapInfo } from '../capstoneAPI';
import type {
  ActiveCapability,
  PSAPInfo,
  PsapState,
  StateWithPsaps,
} from './psapInfo.types';

export type { ActiveCapability, PSAPInfo, PsapState, StateWithPsaps };

/* this works like an action, the creators are automatically produced thanks to createAsyncThunk */
/* they are listed in extra reducers */
export const fetchPsap = createAsyncThunk<PSAPInfo, string>(
  'psap/fetchPsapInfo',
  async (endpoint, { rejectWithValue }) => {
    try {
      const response = await fetchPsapInfo(endpoint);
      return response.data;
    } catch (err) {
      const { message } = normalizeError(err);
      return rejectWithValue(message);
    }
  },
);

export const initialState: PsapState = {
  loading: false,
  hasErrors: false,
  // Most of the codebase makes the assumption that currentPSAP is present
  // and
  currentPSAP: {} as unknown as PSAPInfo,
};

const psapInfo = createSlice({
  name: 'psapInfo',
  initialState,
  reducers: {
    /**
     * WARNING: This should only be used by the debug panel! It does not
     * actually grant a capability to an organization, it only displays the UI
     * as if the capability had been granted.
     */
    setCapability(
      state,
      {
        payload,
      }: {
        payload: {
          name: string;
          value: ActiveCapability | null;
        };
      },
    ) {
      if (payload.value) {
        state.currentPSAP.activeCapabilities[payload.name] = payload.value;
        state.currentPSAP.active_capabilities.push(payload.value);
      } else {
        delete state.currentPSAP.activeCapabilities[payload.name];
        state.currentPSAP.active_capabilities =
          state.currentPSAP.active_capabilities.filter(
            x => x.name != payload.name,
          );
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPsap.fulfilled, (state, action) => {
        state.currentPSAP = {
          ...action.payload,
          activeCapabilities: normalizeCapabilities(
            action.payload.active_capabilities,
          ) as Record<string, ActiveCapability>,
        };
        state.loading = false;
        state.hasErrors = false;
      })
      .addCase(fetchPsap.pending, state => {
        state.loading = true;
      })
      .addCase(fetchPsap.rejected, (state, action) => {
        state.loading = false;
        state.hasErrors = action.payload as string;
      });
  },
  selectors: {
    selectPSAPCountry: (state: PsapState) => state.currentPSAP.country,
    selectPSAPCountryAlpha2: (state: PsapState) =>
      state.currentPSAP.country
        ? alpha3ToAlpha2(state.currentPSAP.country) || 'US'
        : 'US',
  },
});

export const psapSelector = (state: StateWithPsaps) => state.psaps;
export const mapLayerSettingsSelector = (state: StateWithPsaps) =>
  state.psaps.currentPSAP?.map_layer_settings || {};
export const selectActiveCapabilities = (state: StateWithPsaps) =>
  state.psaps.currentPSAP?.activeCapabilities || {};
export const selectIsJVEnabled = createSelector(
  selectActiveCapabilities,
  activeCapabilities => Boolean(activeCapabilities[JURISDICTION_VIEW]),
);

export const { selectPSAPCountry, selectPSAPCountryAlpha2 } =
  psapInfo.getSelectors((state: StateWithPsaps) => state.psaps);

export const { setCapability } = psapInfo.actions;
export const psaps = psapInfo.reducer;
export default psaps;
